import { useState, useEffect, type ReactNode, type MouseEvent } from 'react';
import { Link, useLocation } from 'wouter';
import { Wordmark, BrandLogo } from '../components';
import { InstallAppControl } from './InstallApp';
import { useAuthModal } from '../state';
import { MobileAuthBar } from './MobileAuthBar';
import './MarketingNav.css';

/* ----------------------------------------------------------------------------
   The shared public navigation bar. Five primary menus — three with dropdowns
   of sub-destinations, plus Marketplace and Blog as direct links — and the
   Sign in / Sign up actions. Used on the landing page (transparent variant,
   over the heartwood hero) and on every public marketing page via PublicShell
   (solid variant, so the text stays readable over light backgrounds).
   -------------------------------------------------------------------------- */

type SubLink = { label: string; href: string; note?: string };
type NavEntry =
  | { kind: 'link'; label: string; href: string }
  | { kind: 'menu'; label: string; items: SubLink[] };

const NAV: NavEntry[] = [
  {
    kind: 'menu',
    label: 'How it works',
    items: [
      { label: 'Overview', href: '/how-it-works', note: 'The full step-by-step' },
      { label: 'Why not a chatbot?', href: '/how-it-works#why-not-chatbot' },
      { label: 'What IP really is', href: '/how-it-works#ip-instruments' },
      { label: 'How we protect your idea', href: '/how-it-works#protection' },
      { label: 'How to develop an invention', href: '/how-to-develop-an-invention', note: 'Idea to attorney-ready, step by step' },
      { label: 'Cost to develop an invention', href: '/cost-to-develop-an-invention', note: 'Upfront cost, idea to filing-ready' },
      { label: 'Who can help with an invention', href: '/invention-help', note: 'Attorney, engineer, incubator: when' },
      { label: 'See a sample memo', href: '/demo/memo' },
    ],
  },
  {
    kind: 'menu',
    label: 'Education',
    items: [
      { label: 'Education Center', href: '/learn', note: 'Lessons, paths, and quizzes' },
      { label: 'EntreDash Glossary', href: '/learn/glossary' },
      { label: 'Prepare for a patent attorney', href: '/prepare-for-patent-attorney', note: 'What to bring, what to ask' },
      { label: "Today's Insight", href: '/learn' },
      { label: 'Free Guide', href: '/infographic', note: 'The spark-to-business one-pager' },
      // Moved here from the old Pricing dropdown (now a one-click link).
      { label: 'For Students', href: '/students', note: 'Student pricing and resources' },
      { label: 'Family Bundle', href: '/students#family' },
    ],
  },
  {
    // Pricing dropdown. "Pricing" is the homepage's pay-as-you-go section;
    // "Plan features" and "Compare plans" go to the full /plans page. The
    // comparison link auto-expands the collapsible matrix on arrival (PricingStack
    // reads the #plan-comparison hash). (Family Bundle / For Students live in the
    // Learn menu.)
    kind: 'menu',
    label: 'Pricing',
    items: [
      { label: 'Pricing', href: '/#pricing', note: 'Pay as you go, on the homepage' },
      { label: 'Plan features', href: '/plans#subscription', note: 'What each plan includes' },
      { label: 'Compare plans', href: '/plans#plan-comparison', note: 'Full side-by-side matrix' },
    ],
  },
  { kind: 'link', label: 'Marketplace', href: '/marketplace' },
  { kind: 'link', label: 'Blog', href: '/blog' },
];

function Caret() {
  return (
    <svg className="mnav__caret" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="M2.5 4.5 6 8l3.5-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Poll for a hash target on a freshly navigated page, then scroll to it. */
function scrollToHashWhenReady(hash: string, smooth: boolean, attempts = 0) {
  const el = document.getElementById(hash);
  if (el) {
    el.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
    history.replaceState(null, '', `${window.location.pathname}#${hash}`);
  } else if (attempts < 30) {
    setTimeout(() => scrollToHashWhenReady(hash, smooth, attempts + 1), 40);
  }
}

export function MarketingNav({
  variant = 'transparent',
  rightAction,
}: {
  variant?: 'transparent' | 'solid';
  rightAction?: ReactNode;
}) {
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openAcc, setOpenAcc] = useState<string | null>(null);
  const [, navigate] = useLocation();
  const openAuthModal = useAuthModal((s) => s.openModal);

  // Shrink the wordmark on narrow phones. The wordmark renders as live text
  // sized by an inline font-size, so at the full 44px lockup "EntreDash" is
  // ~280px wide — on a ~360px Android that eats the whole bar and pushes the
  // hamburger off-screen. Dropping to 30px under 600px keeps the burger's slot
  // clear. Mirrors the logged-in AppShell's compact-brand behaviour.
  const [compactBrand, setCompactBrand] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 600px)');
    const apply = () => setCompactBrand(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  // Solidify the bar once the visitor scrolls off the top (transparent variant
  // only — the solid variant is always solid).
  useEffect(() => {
    if (variant === 'solid') return;
    const onScroll = () => setScrolled(window.scrollY > 48);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [variant]);

  // Lock body scroll while the drawer is open.
  useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [drawerOpen]);

  const solid = variant === 'solid' || scrolled;

  // SPA navigation that understands path#hash links. Static blog (.html) and
  // external links fall through to the browser.
  const go = (href: string) => (e: MouseEvent) => {
    if (/^https?:/.test(href) || href.endsWith('.html')) return; // let browser handle
    const [path, hash] = href.split('#');

    // If the hash target already exists on THIS page, just scroll to it —
    // regardless of the current pathname (robust against base paths / the dev
    // preview, where window.location.pathname may not be a bare "/").
    if (hash && document.getElementById(hash)) {
      e.preventDefault();
      setDrawerOpen(false);
      document.getElementById(hash)!.scrollIntoView({ behavior: 'smooth', block: 'start' });
      history.replaceState(null, '', `${window.location.pathname}#${hash}`);
      return;
    }

    // Otherwise it's a cross-page link: navigate, then jump to the section
    // (if any) once the new page has mounted.
    e.preventDefault();
    setDrawerOpen(false);
    navigate(path || '/');
    if (hash) scrollToHashWhenReady(hash, false);
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <header className={`mnav${solid ? ' mnav--solid' : ''}`}>
      <div className="mnav__inner">
        <div className="mnav__brand">
          <Link href="/" className="mnav__wordmark" aria-label="EntreDash home">
            {/* 44 (was 37): wordmark cap-height ~22-24px, matching Zoom's nav
                logo prominence, in the 72px bar (leaves ~14px clear top/bottom).
                onDark in the transparent state (over the green hero) so "Entre"
                is white; in the solid (cream) state it's black. */}
            <BrandLogo size={compactBrand ? 30 : 44} onDark={!solid} />
          </Link>
          {/* Definitional gloss on the coined wordmark: Entre = Entrepreneur,
              Dash = Dashboard. Deliberately subordinate — a smaller, tracked
              two-line Switzer stack behind a hairline divider, neutral only (no
              brass, no green: those stay the earned accent and the wordmark's
              own). Its two words fusing into one echo the decode. Hidden once
              the bar tightens (see MarketingNav.css) so it never crowds the
              centered menu or the hamburger. */}
          <span className="mnav__sep" aria-hidden="true" />
          <span className="mnav__descriptor">
            <span>Entrepreneur</span>
            <span>Dashboard</span>
          </span>
        </div>

        <nav className="mnav__links" aria-label="Primary">
          {NAV.map((entry) =>
            entry.kind === 'link' ? (
              <span className="mnav__item" key={entry.label}>
                <a className="mnav__link" href={entry.href} onClick={go(entry.href)}>
                  {entry.label}
                </a>
              </span>
            ) : (
              <div className="mnav__item" key={entry.label}>
                {/* The trigger is also a link (to the first item) so it works
                    with keyboard + click, while hover/focus reveals the menu. */}
                <a
                  className="mnav__link"
                  href={entry.items[0].href}
                  onClick={go(entry.items[0].href)}
                  aria-haspopup="true"
                >
                  {entry.label}
                  <Caret />
                </a>
                <div className="mnav__dropdown" role="menu">
                  {entry.items.map((sub) => (
                    <a
                      key={sub.label}
                      className="mnav__dropdown-link"
                      href={sub.href}
                      onClick={go(sub.href)}
                      role="menuitem"
                    >
                      {sub.label}
                      {sub.note && <span className="mnav__dropdown-note">{sub.note}</span>}
                    </a>
                  ))}
                </div>
              </div>
            ),
          )}
        </nav>

        <div className="mnav__actions">
          {rightAction ?? (
            <>
              {/* Open the popup instead of navigating to a separate auth page. */}
              <button
                type="button"
                className="mnav__signin"
                onClick={() => openAuthModal('signin')}
              >
                Sign in
              </button>
              <button
                type="button"
                className="mnav__try"
                onClick={() => openAuthModal('signup')}
              >
                Sign up
              </button>
            </>
          )}
        </div>

        <button
          type="button"
          className="mnav__burger"
          aria-label="Open menu"
          aria-expanded={drawerOpen}
          onClick={() => setDrawerOpen(true)}
        >
          <span />
          <span />
          <span />
        </button>
      </div>

      {/* Mobile drawer with accordion sub-menus */}
      {drawerOpen && (
        <>
          <div className="mnav__scrim" onClick={() => setDrawerOpen(false)} />
          <div className="mnav__drawer" role="dialog" aria-label="Menu">
            <div className="mnav__drawer-top">
              <Wordmark size={24} />
              <button
                type="button"
                className="mnav__close"
                aria-label="Close menu"
                onClick={() => setDrawerOpen(false)}
              >
                ✕
              </button>
            </div>

            {NAV.map((entry) =>
              entry.kind === 'link' ? (
                <a
                  key={entry.label}
                  className="mnav__drawer-link"
                  href={entry.href}
                  onClick={go(entry.href)}
                >
                  {entry.label}
                </a>
              ) : (
                <div
                  className="mnav__acc"
                  key={entry.label}
                  data-open={openAcc === entry.label}
                >
                  <button
                    type="button"
                    className="mnav__acc-head"
                    aria-expanded={openAcc === entry.label}
                    onClick={() =>
                      setOpenAcc((cur) => (cur === entry.label ? null : entry.label))
                    }
                  >
                    {entry.label}
                    <Caret />
                  </button>
                  <div className="mnav__acc-body">
                    <div>
                      {entry.items.map((sub) => (
                        <a
                          key={sub.label}
                          className="mnav__acc-link"
                          href={sub.href}
                          onClick={go(sub.href)}
                        >
                          {sub.label}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              ),
            )}

            {/* Install-as-app affordance. Renders nothing when already
                running standalone; otherwise fires the native install prompt
                (Android / desktop Chrome) or shows Add-to-Home-Screen steps
                (iOS Safari and other browsers). */}
            <InstallAppControl
              className="mnav__install"
              onActivate={() => setDrawerOpen(false)}
            >
              <span className="mnav__install-icon" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 3v12" />
                  <path d="M8 11l4 4 4-4" />
                  <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
                </svg>
              </span>
              Install app
            </InstallAppControl>

            <div className="mnav__drawer-secondary">
              <button
                type="button"
                onClick={() => {
                  setDrawerOpen(false);
                  openAuthModal('signin');
                }}
              >
                Sign in
              </button>
            </div>
            <button
              type="button"
              className="mnav__drawer-try"
              onClick={() => {
                setDrawerOpen(false);
                openAuthModal('signup');
              }}
            >
              Sign up
            </button>
          </div>
        </>
      )}
      {/* Sign up / Sign in pinned to the bottom on phones for signed-out visitors (not when a page overrides the actions). */}
      {!rightAction && <MobileAuthBar drawerOpen={drawerOpen} />}
    </header>
  );
}
